/**
 * @file PhoneVerificationModal.jsx — multi-step booking phone/email verification.
 */
import React, { useState, useEffect } from "react";
import { Phone as PhoneIcon, Mail as MailIcon, AlertCircle, Info } from "lucide-react";
import { authService } from "../services/auth";
import { useAuth } from "../hooks/useAuth";
import { COUNTRY_CODES, formatLocalNumber } from "../../../shared/country-codes";
import OAuthButtons from "./OAuthButtons";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogBody,
  DialogFooter,
  Input,
  Select,
  SelectItem,
  toast,
} from "../ui";
import "./PhoneVerificationModal.css";

const buildPhone = (countryCode, digits) => countryCode + digits.replace(/\D/g, "");

export default function PhoneVerificationModal({
  open,
  onClose,
  onVerified,
  requireEmailVerification = false,
}) {
  const { user, handleAuthSuccess } = useAuth();
  const isLoggedIn = !!user;

  const [step, setStep] = useState("phone");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [countryCode, setCountryCode] = useState("+52");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [code, setCode] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isExisting, setIsExisting] = useState(false);
  const [mergePending, setMergePending] = useState(false);

  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [pendingAuthData, setPendingAuthData] = useState(null);

  useEffect(() => {
    if (open) {
      if (user?.isPhoneVerified && user?.email) {
        if (requireEmailVerification && !user.isEmailVerified) {
          setStep("emailCode");
        } else {
          setStep("phone");
        }
      } else if (user?.isPhoneVerified && !user?.email) {
        setStep("email");
      } else {
        setStep("phone");
      }
      setPhoneDigits("");
      setCode("");
      setError("");
      setAcceptTerms(false);
      setIsExisting(false);
      setMergePending(false);
      setFirstName("");
      setLastName("");
      setEmail("");
      setEmailCode("");
      setPendingAuthData(null);
    }
  }, [open, user, requireEmailVerification]);

  const clearError = () => setError("");
  const fullPhone = buildPhone(countryCode, phoneDigits);

  const proceedAfterPhone = (authData) => {
    handleAuthSuccess(authData);
    const authUser = authData?.data?.user || authData?.user;
    if (authUser?.email) {
      if (requireEmailVerification && !authUser.isEmailVerified) {
        setEmail(authUser.email);
        setPendingAuthData(authData);
        setStep("emailCode");
        authService.addEmail(authUser.email).catch(() => null);
      } else {
        onVerified(authData);
      }
    } else {
      setPendingAuthData(authData);
      setStep("email");
    }
  };

  const handlePhoneSubmit = async () => {
    if (!phoneDigits.trim()) return setError("Ingresa tu número de teléfono");
    setLoading(true);
    clearError();
    try {
      if (isLoggedIn) {
        const res = await authService.addPhone(fullPhone);
        setMergePending(!!res.data?.data?.mergePending);
        setIsExisting(true);
        setStep("code");
      } else {
        await authService.requestPhoneCode(fullPhone);
        setIsExisting(true);
        setStep("code");
      }
    } catch (err) {
      if (!isLoggedIn && err.response?.status === 404) {
        setIsExisting(false);
        setStep("name");
      } else {
        setError(err.response?.data?.message || "Error al enviar el código");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      return setError("Ingresa tu nombre y apellido");
    }
    if (!acceptTerms) {
      return setError("Debes aceptar los términos y condiciones");
    }
    setLoading(true);
    clearError();
    try {
      await authService.patientPhoneRegister({
        phone: fullPhone,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        acceptTerms: true,
      });
      setStep("code");
    } catch (err) {
      setError(err.response?.data?.message || "Error al crear la cuenta");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    if (code.length !== 6) return setError("El código debe tener 6 dígitos");
    setLoading(true);
    clearError();
    try {
      const res = await authService.verifyPhoneCode(fullPhone, code.trim());
      const wasMerged = res.data?.data?.merged;
      toast.success(
        wasMerged
          ? "Teléfono verificado. Tus citas anteriores fueron vinculadas a esta cuenta."
          : isLoggedIn
          ? "¡Teléfono verificado!"
          : isExisting
          ? "¡Teléfono verificado!"
          : "¡Cuenta creada y teléfono verificado!"
      );
      proceedAfterPhone(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Código inválido o expirado");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email.trim())) {
      return setError("Ingresa un correo electrónico válido");
    }
    setLoading(true);
    clearError();
    try {
      await authService.addEmail(email.trim());
      if (requireEmailVerification) {
        setStep("emailCode");
      } else {
        toast.success("¡Correo guardado!");
        onVerified(pendingAuthData);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error al enviar el código");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailCodeSubmit = async () => {
    if (emailCode.length !== 6) return setError("El código debe tener 6 dígitos");
    setLoading(true);
    clearError();
    try {
      const res = await authService.verifyEmailCode(emailCode.trim());
      toast.success("¡Correo verificado!");
      onVerified(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Código inválido o expirado");
    } finally {
      setLoading(false);
    }
  };

  const handleResendPhoneCode = async () => {
    setLoading(true);
    clearError();
    try {
      if (isLoggedIn) await authService.addPhone(fullPhone);
      else await authService.requestPhoneCode(fullPhone);
      toast.info("Código reenviado a tu WhatsApp");
    } catch {
      setError("No se pudo reenviar el código");
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmailCode = async () => {
    setLoading(true);
    clearError();
    try {
      await authService.addEmail(email.trim());
      toast.info("Código reenviado a tu correo");
    } catch {
      setError("No se pudo reenviar el código");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = (authData) => {
    handleAuthSuccess(authData);
    onVerified(authData);
  };

  const isEmailStep = step === "email" || step === "emailCode";

  const dialogTitle = {
    phone: isLoggedIn ? "Verifica tu WhatsApp" : "Ingresa tu teléfono",
    name: "Crea tu cuenta",
    code: "Verifica tu WhatsApp",
    email: "Ingresa tu correo",
    emailCode: "Confirma tu correo",
  }[step];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        size="sm"
        title={
          <span className="pvm-title">
            {isEmailStep ? <MailIcon size={18} /> : <PhoneIcon size={18} />} {dialogTitle}
          </span>
        }
      >
        <DialogBody>
          {error && (
            <div className="auth-alert auth-alert-error" style={{ marginBottom: 12 }}>
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          {step === "phone" && (
            <div className="pvm-step">
              <p className="pvm-hint">
                {isLoggedIn
                  ? "Ingresa tu número de WhatsApp para verificar tu cuenta y poder agendar citas."
                  : "Te enviaremos un código de verificación por WhatsApp."}
              </p>
              <div className="pvm-row">
                <div style={{ width: 130, flexShrink: 0 }}>
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    {COUNTRY_CODES.map((c) => (
                      <SelectItem key={c.code + c.label} value={c.code}>
                        {c.flag} {c.code} ({c.label})
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                <Input
                  autoFocus
                  type="tel"
                  label="Número"
                  placeholder="55 1234 5678"
                  value={formatLocalNumber(countryCode, phoneDigits)}
                  onChange={(e) => setPhoneDigits(e.target.value.replace(/\D/g, ""))}
                />
              </div>
            </div>
          )}

          {step === "name" && (
            <div className="pvm-step">
              <div className="auth-alert auth-alert-info">
                <Info size={14} />
                <span>No encontramos una cuenta con ese número. Ingresa tu nombre para registrarte.</span>
              </div>
              <Input autoFocus label="Nombre" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              <Input label="Apellido" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              <Checkbox
                label={
                  <span>
                    Acepto los{" "}
                    <a href="https://consultto.mx/terminos" target="_blank" rel="noreferrer" className="auth-link">
                      Términos y Condiciones
                    </a>{" "}
                    y el{" "}
                    <a href="https://consultto.mx/aviso-de-privacidad" target="_blank" rel="noreferrer" className="auth-link">
                      Aviso de Privacidad
                    </a>
                  </span>
                }
                checked={acceptTerms}
                onCheckedChange={(c) => setAcceptTerms(!!c)}
              />
            </div>
          )}

          {step === "code" && (
            <div className="pvm-step">
              <p className="pvm-hint">
                Enviamos un código de 6 dígitos a <strong>{fullPhone}</strong> por WhatsApp.
              </p>
              {mergePending && (
                <div className="auth-alert auth-alert-info">
                  <Info size={14} />
                  <span>Encontramos citas anteriores con este número. Se vincularán a tu cuenta al verificar.</span>
                </div>
              )}
              <input
                className="auth-code-input"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && code.length === 6) handleCodeSubmit();
                }}
                placeholder="000000"
              />
              <div className="pvm-mini-actions">
                <Button variant="ghost" size="sm" onClick={handleResendPhoneCode} disabled={loading}>
                  Reenviar código
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setCode(""); setStep("phone"); }} disabled={loading}>
                  Cambiar número
                </Button>
              </div>
            </div>
          )}

          {step === "email" && (
            <div className="pvm-step">
              <p className="pvm-hint">
                Ingresa tu correo electrónico para recibir la confirmación y recordatorios de tu cita.
              </p>
              <Input
                autoFocus
                type="email"
                label="Correo electrónico"
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleEmailSubmit();
                }}
              />
              <div className="auth-divider">o</div>
              <OAuthButtons
                role="PACIENTE"
                onSuccess={handleGoogleSuccess}
                onError={(msg) => setError(msg)}
              />
            </div>
          )}

          {step === "emailCode" && (
            <div className="pvm-step">
              <p className="pvm-hint">
                Enviamos un código de 6 dígitos a <strong>{email.trim()}</strong>.
              </p>
              <input
                className="auth-code-input"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && emailCode.length === 6) handleEmailCodeSubmit();
                }}
                placeholder="000000"
              />
              <div className="pvm-mini-actions">
                <Button variant="ghost" size="sm" onClick={handleResendEmailCode} disabled={loading}>
                  Reenviar código
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setEmailCode(""); setStep("email"); }} disabled={loading}>
                  Cambiar correo
                </Button>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          {step === "phone" && (
            <Button onClick={handlePhoneSubmit} loading={loading} disabled={!phoneDigits.trim()}>
              Continuar
            </Button>
          )}
          {step === "name" && (
            <Button onClick={handleNameSubmit} loading={loading}>
              Registrarme
            </Button>
          )}
          {step === "code" && (
            <Button onClick={handleCodeSubmit} loading={loading} disabled={code.length !== 6}>
              Verificar
            </Button>
          )}
          {step === "email" && (
            <Button onClick={handleEmailSubmit} loading={loading} disabled={!email.trim()}>
              Continuar
            </Button>
          )}
          {step === "emailCode" && (
            <Button onClick={handleEmailCodeSubmit} loading={loading} disabled={emailCode.length !== 6}>
              Verificar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
